import { createAsyncThunk } from '@reduxjs/toolkit'
import { PaymentInfo } from 'typings/payment'
import paymentAPI from 'services/payment'
import { ServerError } from 'typings/serverError'
import type { OriginalRootState } from 'redux/rootReducer'
import { selectActiveAccountId } from 'redux/demographic/demographicSlice'
import { selectAccessToken } from 'redux/authToken/authTokenSlice'
import { selectViewPaymentPagination } from './paymentHistorySelectors'
// eslint-disable-next-line import/no-cycle
import { updatePaymentList } from './paymentHistorySlice'

export const fetchPaymentList = createAsyncThunk(
  'paymentHistorySlice/fetchPaymentList',
  async (
    payload: Pick<PaymentInfo, 'limit' | 'currentPage'>,
    { getState, rejectWithValue }
  ) => {
    const currentState = getState() as OriginalRootState
    const accountId = selectActiveAccountId(currentState)
    const accessToken = selectAccessToken(currentState)

    try {
      return await paymentAPI.getPaymentsList({
        ...payload,
        accountId,
        accessToken
      })
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

/**
 * Fetch payment list using current payment list state (page number and page size)
 */
export const refreshPaymentList = createAsyncThunk(
  'paymentHistorySlice/refreshPaymentList',
  async (payload, { getState }) => {
    const currentState = getState() as OriginalRootState
    const accountId = selectActiveAccountId(currentState)
    const accessToken = selectAccessToken(currentState)
    const { limit, currentPage } = selectViewPaymentPagination(currentState)
    const newPayload = {
      accessToken,
      accountId,
      limit,
      currentPage
    }
    return paymentAPI.getPaymentsList(newPayload)
  }
)

/**
 * @deprecated use {@link fetchPaymentList} instead
 */
export const getPaymentList = createAsyncThunk(
  'paymentHistorySlice/getPaymentList',
  async (payload: PaymentInfo, { dispatch }) => {
    try {
      const response = await paymentAPI.getPaymentsList(payload)
      const { payments, currentPage, pageSize, totalPayments } = response

      dispatch(
        updatePaymentList({
          paymentCount: totalPayments,
          dataList: payments,
          gridOption: { ...payload, currentPage, limit: pageSize },
          isDataLoading: false,
          isPaymentResponseFail: false,
          errorMessage: ''
        })
      )
    } catch (error) {
      const { response, statusCode, businessErrorCode } = error as ServerError
      dispatch(
        updatePaymentList({
          isPaymentResponseFail: true,
          isDataLoading: false,
          errorMessage: response?.message,
          dataList: [],
          error: businessErrorCode,
          status: statusCode
        })
      )
    }
  }
)

export const getOneTimePaymentList = createAsyncThunk(
  'paymentHistorySlice/getOneTimePaymentList',
  async (payload: PaymentInfo, { dispatch }) => {
    try {
      const response = await paymentAPI.getOneTimePaymentList(payload)
      const {
        payments,
        pageMetadata: { currentPage, pageSize, totalPayments }
      } = response

      dispatch(
        updatePaymentList({
          paymentCount: totalPayments,
          dataList: payments,
          gridOption: { ...payload, currentPage, limit: pageSize },
          isDataLoading: false,
          isPaymentResponseFail: false,
          errorMessage: ''
        })
      )
    } catch (error) {
      const { response, statusCode, businessErrorCode } = error as ServerError
      dispatch(
        updatePaymentList({
          isPaymentResponseFail: true,
          isDataLoading: false,
          errorMessage: response?.message,
          dataList: [],
          error: businessErrorCode,
          status: statusCode
        })
      )
    }
  }
)
